import './App.css';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { setUser } from './features/auth/authSlice';
import storageManager from './utils/storageManager';
import { connectSocket, disconnectSocket } from './services/socket';
import SignupPage from './pages/auth/SignupPage';
import { MainPage } from './pages/LandingPage';
import AdditionRequestPage from './pages/auth/AdditionRequestPage';
import WaitingVerificationPage from './pages/auth/WaitingVerificationPage';
import LoginSelectionPage from './pages/auth/LoginSelectionPage';
import LoginPage from './pages/auth/LoginPage';
import LoginPlayerPage from './pages/auth/LoginPlayerPage';
import AboutUs from './pages/AboutUs';
import TermsPage from './pages/TermsPage';
import ProfileApprovedPage from './pages/auth/ProfileApprovedPage';
import IdentityVerificationPage from './pages/auth/IdentityVerificationPage';
import RegisterCoachPage from './pages/auth/RegisterCoachPage';
import RegisterScoutPage from './pages/auth/RegisterScoutPage';
import ComingSoonPage from './pages/auth/ComingSoonPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import PlansSelectionPage from './pages/auth/PlansSelectionPage';
import PlayerPlanSelectionPage from './pages/auth/PlayerPlanSelectionPage';
import RegistrationSuccessPage from './pages/auth/RegistrationSuccessPage';
import RegistrationCancelPage from './pages/auth/RegistrationCancelPage';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import PlayerDashboard from './pages/dashboard/PlayerDashboard';
import PricingPage from './pages/dashboard/PricingPage';
import ForPlayers from './pages/dashboard/ForPlayers';
import ForCoaches from './pages/dashboard/ForCoaches';
import SuccessStories from './pages/dashboard/SuccessStories/SuccessStories';
import MessagesPage from './pages/MessagesPage';
import PlayerTeamPage from './pages/player/team/PlayerTeamPage';
import PlayerViewProfile from './pages/player/profile/PlayerProfileView';
import PlayerEditProfile from './pages/player/profile/PlayerEditProfile';

// Layout Imports
import { PlayerLayout } from './layouts/PlayerLayout';
import { CoachLayout } from './layouts/CoachLayout';
import { CoachDashboardLayout } from './layouts/CoachDashboardLayout';
import { ScoutLayout } from './layouts/ScoutLayout';
import { ScoutDashboardLayout } from './layouts/ScoutDashboardLayout';
import { AdminLayout } from './layouts/AdminLayout';
import AdminDashboardLayout from './layouts/AdminDashboardLayout';

// Coach Dashboard Imports
import CoachDashboard from './pages/coach/CoachDashboard';
import PlayerProfileView from './pages/coach/playerProfile/PlayerProfileView';
import { UncommittedTracker } from './pages/coach/UncommittedTracker/MainUncommitedScreen';
import { CoachTeamsSection } from './pages/coach/coachTeams/CoachTeamsSection';
import { CoachTeamProfilePage } from './pages/coach/team/CoachTeamProfilePage';
import MainStatisticsScreen from './pages/coach/statistics/MainStatisticsScreen';
import { StaffSection } from './pages/coach/staff/StaffSection';
import { TeamAnalysisPage } from './pages/coach/teamAnalysis/TeamAnalysisPage';
import { CoachProfile } from './components/coach/dashboard/CoachProfile';
import AdminPlayerProfileView from './pages/admin/profile/AdminPlayerProfileView';

// Scout Dashboard Imports
import ScoutDashboard from './pages/scout/ScoutDashboard';
import ScoutPlayerProfileView from './pages/scout/playerProfile/PlayerProfileView';
import { UncommittedTracker as ScoutUncommittedTracker } from './pages/scout/UncommittedTracker/MainUncommitedScreen';
import { DreamTeamDashboard } from './pages/scout/scoutTeams/DreamTeam/DreamTeamDashboard';
import { ScoutTeamProfilePage } from './pages/scout/scoutTeams/ScoutTeamProfilePage';
import ScoutMainStatisticsScreen from './pages/scout/Statistics/MainStatisticsScreen';
import { TeamAnalysisPage as ScoutTeamAnalysisPage } from './pages/scout/TeamAnalysis/TeamAnalysisPage';
import { ScoutProfile } from './pages/scout/dashboard/ScoutProfile';

// Admin Dashboard Imports
import AdminDashboard from './pages/admin/AdminDashboard';
import { UpcomingGamesPage } from './pages/admin/upcomingGames/UpcomingGamesPage';
import { ScrapeJobsPage } from './pages/admin/scrapeJobs/ScrapeJobsPage';
import { ProfileVerificationsPage } from './pages/admin/profileVerifications/ProfileVerificationsPage';
import { ManualEditsPage } from './pages/admin/manualEdits/ManualEditsPage';
import { CommitmentScannerPage } from './pages/admin/commitmentScanner/CommitmentScannerPage';
import { NotificationsPage } from './pages/admin/notifications/NotificationsPage';
import { HomePagePostsPage } from './pages/admin/homePage/HomePagePostsPage';

// Auth Components
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { LandingRoute } from './components/auth/LandingRoute';

// Support Page
import SupportPage from './pages/SupportPage';
// Blurred player and team previews, opened from the homepage leaderboard
import { PlayerPreviewPage, TeamPreviewPage } from './pages/preview/ProfilePreviewPages';

// Theme
import { ThemeProvider } from './components/common/ThemeProvider';

// Layout wrapper for pages with main Navbar and footer
const MainLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
};

function App() {
  const dispatch = useDispatch();

  // Initialize auth state from localStorage on app load
  useEffect(() => {
    const token = storageManager.getToken();
    const user = storageManager.getUser();

    if (token && user) {
      // Restore user session from localStorage
      dispatch(setUser(user));
      console.log('User session restored:', user.role);
      connectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [dispatch]);

  return (
    <BrowserRouter>
      <ThemeProvider>
      <div className="App">
        <Routes>
          {/* Main Routes with Navbar */}
          <Route path="/" element={<LandingRoute><MainLayout><MainPage /></MainLayout></LandingRoute>} />
          <Route path="/about" element={<LandingRoute><MainLayout><AboutUs /></MainLayout></LandingRoute>} />
          <Route path="/pricing" element={<LandingRoute><MainLayout><PricingPage /></MainLayout></LandingRoute>} />
          <Route path="/for-players" element={<LandingRoute><MainLayout><ForPlayers /></MainLayout></LandingRoute>} />
          <Route path="/for-coaches" element={<LandingRoute><MainLayout><ForCoaches /></MainLayout></LandingRoute>} />
          <Route path="/success-stories" element={<LandingRoute><MainLayout><SuccessStories /></MainLayout></LandingRoute>} />
          <Route path="/support" element={<MainLayout><SupportPage /></MainLayout>} />
          <Route path="/preview/player/:playerId" element={<MainLayout><PlayerPreviewPage /></MainLayout>} />
          <Route path="/preview/team/:teamId" element={<MainLayout><TeamPreviewPage /></MainLayout>} />
          <Route path="/terms" element={<LandingRoute><MainLayout><TermsPage /></MainLayout></LandingRoute>} />

          {/* Player Routes with PlayerLayout */}
          <Route path="/player" element={<ProtectedRoute allowedRoles={['player']}><PlayerLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/player/dashboard" replace />} />
            <Route path="dashboard" element={<PlayerDashboard />} />
            <Route path="edit-profile" element={<PlayerEditProfile />} />
            <Route path="profile" element={<PlayerDashboard />} />
            <Route path="profile/:playerId" element={<PlayerViewProfile />} />
            <Route path="team/:teamId" element={<PlayerTeamPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="recruiting" element={<PlayerDashboard />} />
            <Route path="support" element={<SupportPage isDashboard />} />
          </Route>

          {/* Coach Routes with CoachLayout */}
          <Route path="/coach" element={<ProtectedRoute allowedRoles={['coach', 'scout']}><CoachLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/coach/dashboard" replace />} />

            {/* Coach Dashboard Routes with Sidebar */}
            <Route element={<CoachDashboardLayout />}>
              <Route path="dashboard" element={<CoachDashboard />} />
              <Route path="edit-profile" element={<CoachProfile />} />
              <Route path="teams" element={<CoachTeamsSection />} />
              <Route path="team/:teamId" element={<CoachTeamProfilePage />} />
              <Route path="uncommitted-tracker" element={<UncommittedTracker />} />
              <Route path="team-analysis" element={<TeamAnalysisPage />} />
              <Route path="statistics" element={<MainStatisticsScreen />} />
              <Route path="staff" element={<StaffSection />} />
              <Route path="player/:playerId" element={<PlayerProfileView />} />
            </Route>

            <Route path="messages" element={<MessagesPage />} />
            <Route path="support" element={<SupportPage isDashboard />} />
          </Route>

          {/* Scout Routes with ScoutLayout */}
          <Route path="/scout" element={<ProtectedRoute allowedRoles={['scout']}><ScoutLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/scout/dashboard" replace />} />

            {/* Scout Dashboard Routes with Sidebar */}
            <Route element={<ScoutDashboardLayout />}>
              <Route path="dashboard" element={<ScoutDashboard />} />
              <Route path="edit-profile" element={<ScoutProfile />} />
              <Route path="teams" element={<DreamTeamDashboard />} />
              <Route path="team/:teamId" element={<ScoutTeamProfilePage />} />
              <Route path="uncommitted-tracker" element={<ScoutUncommittedTracker />} />
              <Route path="team-analysis" element={<ScoutTeamAnalysisPage />} />
              <Route path="statistics" element={<ScoutMainStatisticsScreen />} />
              <Route path="player/:playerId" element={<ScoutPlayerProfileView />} />
            </Route>

            <Route path="messages" element={<MessagesPage />} />
            <Route path="support" element={<SupportPage isDashboard />} />
          </Route>

          {/* Admin Routes with AdminLayout */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'superAdmin']}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />

            {/* Admin Dashboard Routes with Sidebar */}
            <Route element={<AdminDashboardLayout />}>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="upcoming-games" element={<UpcomingGamesPage />} />
              <Route path="scrape-jobs" element={<ScrapeJobsPage />} />
              <Route path="profile-verifications" element={<ProfileVerificationsPage />} />
              <Route path="manual-edits" element={<ManualEditsPage />} />
              <Route path="commitment-scanner" element={<CommitmentScannerPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="home-page" element={<HomePagePostsPage />} />
              <Route path="users" element={<div className="p-6 text-jp-ink">Admin Users - Coming Soon</div>} />
              <Route path="players" element={<div className="p-6 text-jp-ink">Admin Players - Coming Soon</div>} />
              <Route path="coaches" element={<div className="p-6 text-jp-ink">Admin Coaches - Coming Soon</div>} />
              <Route path="scouts" element={<div className="p-6 text-jp-ink">Admin Scouts - Coming Soon</div>} />
              <Route path="settings" element={<div className="p-6 text-jp-ink">Admin Settings - Coming Soon</div>} />
              <Route path="player/:userId" element={<AdminPlayerProfileView />} />
              <Route path="coach/:userId" element={<div className="p-6 text-jp-ink">Coach Profile View - Coming Soon</div>} />
              <Route path="scout/:userId" element={<div className="p-6 text-jp-ink">Scout Profile View - Coming Soon</div>} />
              <Route path="support" element={<SupportPage isDashboard />} />
            </Route>
          </Route>

          {/* Auth Routes with SimpleNavbar (already included in each page) */}
          <Route path="/auth/signup" element={<PublicRoute><MainLayout><SignupPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/login-selection" element={<PublicRoute><MainLayout><LoginSelectionPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/login" element={<PublicRoute><MainLayout><LoginPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/login-player" element={<PublicRoute><MainLayout><LoginPlayerPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/addition-request" element={<PublicRoute><MainLayout><AdditionRequestPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/register-coach" element={<PublicRoute><MainLayout><RegisterCoachPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/register-scout" element={<PublicRoute><RegisterScoutPage /></PublicRoute>} />
          <Route path="/auth/plans-selection" element={<PublicRoute><MainLayout><PlansSelectionPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/player-plans" element={<PublicRoute><MainLayout><PlayerPlanSelectionPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/forgot-password" element={<PublicRoute><MainLayout><ForgotPasswordPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/coming-soon" element={<PublicRoute><ComingSoonPage /></PublicRoute>} />
          <Route path="/auth/identity-verification" element={<PublicRoute><MainLayout><IdentityVerificationPage /></MainLayout></PublicRoute>} />
          <Route path="/auth/waiting-verification" element={<PublicRoute><WaitingVerificationPage /></PublicRoute>} />
          <Route path="/auth/profile-approved" element={<PublicRoute><ProfileApprovedPage /></PublicRoute>} />

          {/* Registration Success/Cancel Routes (no auth guard during payment flow) */}
          <Route path="/registration/success" element={<RegistrationSuccessPage />} />
          <Route path="/registration/cancel" element={<RegistrationCancelPage />} />

          {/* Redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
